import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { VisitReconciliationStrip } from '@/components/wo/VisitReconciliationStrip';
import { api, API_BASE, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfidenceChip } from '@/components/common/ConfidenceChip';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { CheckCircle2, Sparkles, PlayCircle, ChevronDown, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function WorkOrderVerifyPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [wo, setWo] = useState(null);
  const [ext, setExt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [attachMode, setAttachMode] = useState('new'); // 'new' | 'existing'
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const loadProjects = () => api.get('/master-projects').then(r => {
    // Only unlinked (or WO-less) projects are candidates for attachment.
    const rows = (r.data?.rows || []).filter(p => !p.work_order_number && !p.wo_id);
    setExistingProjects(rows);
  }).catch(() => setExistingProjects([]));

  const load = () => api.get(`/work_orders/${id}`).then(r => {
    setWo(r.data);
    setExt(r.data.extraction || null);
    setProjectName(prev => prev || (r.data.extraction?.client_name ? `${r.data.extraction.client_name} — Wayfinding` : ''));
  }).catch(e => toast.error(parseErr(e)));
  useEffect(() => { load(); loadProjects(); }, [id]);

  const pdfUrl = useMemo(() => wo?.storage_path ? `${API_BASE}/work_orders/${id}/file?auth=${encodeURIComponent(localStorage.getItem('32d.os.token') || '')}` : null, [wo, id]);

  if (!wo) return <AppShell><div className="p-6">Loading…</div></AppShell>;

  const runParse = async () => {
    setBusy(true);
    try { await api.post(`/work_orders/${id}/parse`, {}, { timeout: 180000 }); toast.success('Parsed'); load(); }
    catch (e) { toast.error(parseErr(e)); } finally { setBusy(false); }
  };

  const upd = (path, value) => {
    setExt(prev => {
      const next = structuredClone(prev || {});
      let cur = next; const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts.at(-1)] = value;
      return next;
    });
  };
  const updMilestone = (i, field, value) => setExt(prev => {
    const next = structuredClone(prev);
    next.milestones[i][field] = value; return next;
  });
  const confirmField = (path) => upd(`${path}_confidence`, 1);
  const confirmMilestone = (i) => updMilestone(i, 'confidence', 1);
  const confirmDate = (i) => setExt(prev => { const n = structuredClone(prev); n.key_dates[i].confidence = 1; return n; });

  const verify = async () => {
    if (!ext) return;
    setBusy(true);
    try {
      const payload = { extraction: ext };
      if (wo.client_id) payload.client_id = wo.client_id;
      else payload.new_client_name = ext.client_name;
      await api.post(`/work_orders/${id}/verify`, payload);
      toast.success('Marked verified. Owner can now activate.');
      load();
    } catch (e) { toast.error(parseErr(e)); } finally { setBusy(false); }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      const payload = { assigned_user_ids: [], confirm: false };
      if (attachMode === 'existing') {
        if (!selectedProjectId) {
          toast.error('Please pick an existing project to attach this WO to.');
          setBusy(false);
          return;
        }
        payload.master_project_id = selectedProjectId;
        const p = existingProjects.find(x => x.id === selectedProjectId);
        payload.project_name = p?.project_name || projectName || `WO ${wo.wo_number || ''}`;
      } else {
        payload.project_name = projectName || `${ext.client_name} — Wayfinding`;
      }
      const r = await api.post(`/work_orders/${id}/activate/preview`, payload);
      setPreviewData({ ...r.data, _payload: payload });
      setShowPreview(true);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const confirmActivate = async () => {
    if (!previewData?._payload) return;
    setBusy(true);
    try {
      const payload = { ...previewData._payload, confirm: true };
      const r = await api.post(`/work_orders/${id}/activate`, payload);
      toast.success(previewData.action === 'attach'
        ? 'Attached to project — WO activated'
        : 'New project created — WO activated');
      setShowPreview(false);
      nav(`/projects/${r.data.id}`);
    } catch (e) { toast.error(parseErr(e)); } finally { setBusy(false); }
  };

  const activate = openPreview;  // legacy alias — the button below calls this

  const isOwner = user.role === 'owner';
  const canSeeMoney = ['owner', 'accounts', 'commercials'].includes(user.role);
  const canEnterMoney = ['owner', 'accounts'].includes(user.role);
  const valuesPending = wo?.values_status === 'pending';
  const readOnly = wo.status === 'activated';
  const lowConfMs = (ext?.milestones || []).filter(m => (m.confidence ?? 0) < 0.6).length;
  const lowConfDates = (ext?.key_dates || []).filter(k => (k.confidence ?? 0) < 0.6).length;

  return (
    <AppShell breadcrumbs={[{ label: 'Work Orders', to: '/work-orders' }, { label: `Verify · ${wo.original_filename || 'Draft'}` }]}>
      <div className="p-4 lg:p-6 max-w-[1500px] mx-auto" data-testid="wo-verify-page">
        <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Verify Work Order</h1>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] font-mono">{wo.id}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="chip chip-info">Status: {wo.status}</span>
            {wo.values_status === 'pending' && <span className="chip chip-warning" data-testid="wo-values-pending-chip">Values pending</span>}
            {!ext && wo.storage_path && (
              <Button size="sm" onClick={runParse} disabled={busy} data-testid="wo-parse-button">
                <Sparkles size={14} className="mr-1" />Run AI parse
              </Button>
            )}
            {ext && wo.status !== 'verified' && wo.status !== 'activated' && (
              <Button size="sm" onClick={verify} disabled={busy || lowConfMs > 0 || lowConfDates > 0} data-testid="wo-verify-save-button">                <CheckCircle2 size={14} className="mr-1" />Save &amp; mark verified
              </Button>
            )}
            {wo.status === 'verified' && valuesPending && canEnterMoney && ext?.total_fee_inr > 0 && (
              <Button size="sm" onClick={async () => {
                setBusy(true);
                try {
                  const body = {
                    total_fee_inr: ext.total_fee_inr,
                    per_visit_fee_inr: wo?.visit_terms?.per_visit_fee_inr ?? null,
                    milestone_amounts: (ext.milestones || []).map(m => ({ name: m.name, amount_inr: m.amount_inr })),
                  };
                  await api.post(`/work_orders/${id}/values`, body);
                  toast.success('Values saved. WO ready to activate.');
                  load();
                } catch (e) { toast.error(parseErr(e)); }
                finally { setBusy(false); }
              }} disabled={busy} data-testid="wo-save-values-button">
                <CheckCircle2 size={14} className="mr-1" />Save values
              </Button>
            )}
            {wo.status === 'verified' && isOwner && (
              <Button size="sm" onClick={activate} disabled={busy || valuesPending} data-testid="wo-activate-button">
                <PlayCircle size={14} className="mr-1" />Activate project
              </Button>
            )}
          </div>
        </div>

        {(lowConfMs > 0 || lowConfDates > 0) && (
          <div className="rounded-md border p-3 flex gap-2 text-[13px]" style={{ borderColor: 'hsl(38 70% 82%)', background: 'hsl(var(--warning-bg))', color: 'hsl(var(--warning))' }} data-testid="wo-low-conf-banner">
            <AlertTriangle size={16} className="mt-0.5" />
            <div><b>{lowConfMs + lowConfDates}</b> low-confidence field(s) need explicit confirmation before you can mark verified. Adjust the values and click “Confirm” on each.</div>
          </div>
        )}

        {wo.master_project_id && (
          <div className="mt-3">
            <VisitReconciliationStrip projectId={wo.master_project_id} />
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* PDF preview */}
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-hidden" style={{ borderColor: 'hsl(var(--border))', minHeight: '620px' }} data-testid="wo-pdf-pane">
            <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold text-[13px]">Source document</div>
              <div className="text-[11px] text-[hsl(var(--foreground-subtle))] font-mono">{wo.original_filename}</div>
            </div>
            {pdfUrl ? (
              <iframe title="WO PDF" src={pdfUrl} className="w-full" style={{ height: '620px', border: 0, background: 'white' }} data-testid="wo-pdf-iframe" />
            ) : (
              <div className="p-8 text-center text-[hsl(var(--foreground-muted))]">No file attached to this WO. This is a seed draft — upload a real file to parse.</div>
            )}
          </div>

          {/* Verification form */}
          <div className="border rounded-xl bg-[hsl(var(--card))]" style={{ borderColor: 'hsl(var(--border))' }} data-testid="wo-verify-form">
            <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold text-[13px]">Extracted fields</div>
              <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">Updated {fmtDateTime(wo.updated_at)}</div>
            </div>
            {!ext ? (
              <div className="p-8 text-center text-[hsl(var(--foreground-muted))]">Run AI parse to see extracted fields here.</div>
            ) : (
              <div className="p-4 space-y-4">
                <FieldRow testId="wo-field-client" label="Client" score={ext.client_confidence} onConfirm={() => confirmField('client')} readOnly={readOnly}>
                  <Input value={ext.client_name || ''} onChange={e => upd('client_name', e.target.value)} />
                </FieldRow>
                <FieldRow testId="wo-field-site" label="Site address" score={ext.site_confidence} onConfirm={() => confirmField('site')} readOnly={readOnly}>
                  <Input value={ext.site_address || ''} onChange={e => upd('site_address', e.target.value)} />
                </FieldRow>
                <FieldRow testId="wo-field-scope" label="Scope" score={ext.scope_confidence} onConfirm={() => confirmField('scope')} readOnly={readOnly}>
                  <Textarea value={ext.scope_summary || ''} onChange={e => upd('scope_summary', e.target.value)} rows={3} />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[12px] text-[hsl(var(--foreground-muted))]">WO Number</span>
                    <Input value={ext.wo_number || ''} onChange={e => upd('wo_number', e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="text-[12px] text-[hsl(var(--foreground-muted))]">WO Date</span>
                    <Input type="date" value={ext.wo_date_iso || ''} onChange={e => upd('wo_date_iso', e.target.value)} />
                  </label>
                  {canSeeMoney && (
                    <label className="block">
                      <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Total fee (INR)</span>
                      <Input type="number" value={ext.total_fee_inr ?? ''}
                             disabled={!canEnterMoney}
                             onChange={e => upd('total_fee_inr', Number(e.target.value) || null)}
                             data-testid="wo-total-fee" />
                    </label>
                  )}
                  {canSeeMoney && (
                    <label className="block">
                      <span className="text-[12px] text-[hsl(var(--foreground-muted))]">GST %</span>
                      <Input type="number" value={ext.gst_percent ?? ''}
                             disabled={!canEnterMoney}
                             onChange={e => upd('gst_percent', Number(e.target.value) || null)} />
                    </label>
                  )}
                </div>

                {valuesPending && canEnterMoney && (
                  <div className="rounded-md border p-3 flex items-center gap-2 mt-2" style={{ borderColor: 'hsl(var(--warning))', background: 'hsl(var(--surface-2))' }} data-testid="wo-values-pending-banner">
                    <AlertTriangle size={14} className="text-[hsl(var(--warning))]" />
                    <span className="text-[12.5px]">Values pending — Ops verified structural fields. Enter total fee + per-milestone amounts, then Save values to unlock activation.</span>
                  </div>
                )}
                {valuesPending && !canEnterMoney && user.role === 'operations' && (
                  <div className="rounded-md border p-3 flex items-center gap-2 mt-2" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }} data-testid="wo-values-pending-ops-banner">
                    <AlertTriangle size={14} className="text-[hsl(var(--foreground-muted))]" />
                    <span className="text-[12.5px]">Handed off. Owner or Accounts will complete monetary values before activation. You are not blocked.</span>
                  </div>
                )}

                <details className="border rounded-md" style={{ borderColor: 'hsl(var(--border))' }} open>
                  <summary className="px-3 py-2 text-[13px] font-semibold flex items-center gap-2 cursor-pointer">
                    <ChevronDown size={13} />Milestones ({ext.milestones?.length || 0})
                  </summary>
                  <div className="p-3 space-y-3">
                    {(ext.milestones || []).map((m, i) => (
                      <div key={i} className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`wo-milestone-${i}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <Input value={m.name || ''} onChange={e => updMilestone(i, 'name', e.target.value)} className="font-medium" />
                          </div>
                          <div className="flex items-center gap-2">
                            <ConfidenceChip score={m.confidence} dataTestId={`wo-milestone-conf-${i}`} />
                            {(m.confidence ?? 0) < 0.6 && (
                              <Button size="sm" variant="outline" onClick={() => confirmMilestone(i)} data-testid={`wo-milestone-confirm-${i}`}>Confirm</Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <Input type="number" placeholder="%" value={m.percentage ?? ''} onChange={e => updMilestone(i, 'percentage', Number(e.target.value) || null)} />
                          {canSeeMoney ? (
                            <Input type="number" placeholder="Amount (₹)" value={m.amount_inr ?? ''}
                                   disabled={!canEnterMoney}
                                   onChange={e => updMilestone(i, 'amount_inr', Number(e.target.value) || null)}
                                   data-testid={`wo-milestone-amount-${i}`} />
                          ) : (
                            <div />
                          )}
                          <Input placeholder="Trigger" value={m.trigger || ''} onChange={e => updMilestone(i, 'trigger', e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </details>

                <details className="border rounded-md" style={{ borderColor: 'hsl(var(--border))' }}>
                  <summary className="px-3 py-2 text-[13px] font-semibold flex items-center gap-2 cursor-pointer">
                    <ChevronDown size={13} />Key dates ({ext.key_dates?.length || 0})
                  </summary>
                  <div className="p-3 space-y-2">
                    {(ext.key_dates || []).map((k, i) => (
                      <div key={i} className="grid grid-cols-[1fr_140px_auto_auto] gap-2 items-center" data-testid={`wo-date-${i}`}>
                        <Input value={k.label || ''} onChange={e => setExt(p => { const n = structuredClone(p); n.key_dates[i].label = e.target.value; return n; })} />
                        <Input type="date" value={k.date_iso || ''} onChange={e => setExt(p => { const n = structuredClone(p); n.key_dates[i].date_iso = e.target.value; return n; })} />
                        <ConfidenceChip score={k.confidence} />
                        {(k.confidence ?? 0) < 0.6 ? <Button size="sm" variant="outline" onClick={() => confirmDate(i)}>Confirm</Button> : <div />}
                      </div>
                    ))}
                  </div>
                </details>

                <details className="border rounded-md" style={{ borderColor: 'hsl(var(--border))' }}>
                  <summary className="px-3 py-2 text-[13px] font-semibold flex items-center gap-2 cursor-pointer">
                    <ChevronDown size={13} />Special terms ({ext.special_terms?.length || 0})
                  </summary>
                  <ul className="p-3 pl-8 list-disc text-[13px]">
                    {(ext.special_terms || []).map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </details>

                {wo.status === 'verified' && isOwner && (
                  <div className="border rounded-md p-3 space-y-3" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }}
                       data-testid="wo-activate-panel">
                    <div className="font-heading font-semibold text-[13px]">Activate — attach WO to a project</div>
                    <div className="flex gap-1 border rounded-md p-1 text-[12.5px]" style={{ borderColor: 'hsl(var(--border))' }}>
                      <button
                        onClick={() => setAttachMode('new')}
                        className={`flex-1 py-1.5 rounded transition-colors ${attachMode === 'new' ? 'bg-[hsl(var(--card))] font-semibold shadow-sm' : 'text-[hsl(var(--foreground-muted))]'}`}
                        data-testid="wo-attach-mode-new"
                      >
                        Create new project
                      </button>
                      <button
                        onClick={() => setAttachMode('existing')}
                        disabled={existingProjects.length === 0}
                        className={`flex-1 py-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${attachMode === 'existing' ? 'bg-[hsl(var(--card))] font-semibold shadow-sm' : 'text-[hsl(var(--foreground-muted))]'}`}
                        data-testid="wo-attach-mode-existing"
                        title={existingProjects.length === 0 ? 'No unlinked projects available' : ''}
                      >
                        Attach to existing{existingProjects.length ? ` (${existingProjects.length})` : ''}
                      </button>
                    </div>
                    {attachMode === 'new' ? (
                      <label className="block">
                        <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Project name</span>
                        <Input data-testid="wo-activate-project-name" value={projectName} onChange={e => setProjectName(e.target.value)} />
                      </label>
                    ) : (
                      <label className="block">
                        <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Choose an unlinked project</span>
                        <select
                          data-testid="wo-attach-project-select"
                          value={selectedProjectId}
                          onChange={e => setSelectedProjectId(e.target.value)}
                          className="mt-1 w-full h-9 border rounded-md px-2 text-[13px]"
                          style={{ borderColor: 'hsl(var(--border))' }}
                        >
                          <option value="">— Select a project —</option>
                          {existingProjects.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.project_name} · {p.client}{p.code ? ` · ${p.code}` : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">
                          Only projects without an existing Work Order are shown. Attaching preserves any milestones or fields the project already has.
                        </p>
                      </label>
                    )}
                    <Button
                      onClick={activate}
                      disabled={busy ||
                        (attachMode === 'new' && !projectName.trim()) ||
                        (attachMode === 'existing' && !selectedProjectId)}
                      className="w-full"
                      data-testid="wo-activate-button-final"
                    >
                      <PlayCircle size={14} className="mr-1" />
                      {attachMode === 'existing' ? 'Attach & activate' : 'Create project & activate'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showPreview && previewData && (
        <ActivatePreviewDialog data={previewData} busy={busy}
                                onCancel={() => setShowPreview(false)}
                                onConfirm={confirmActivate} />
      )}
    </AppShell>
  );
}

function ActivatePreviewDialog({ data, busy, onCancel, onConfirm }) {
  const action = data.action;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
         data-testid="activate-preview-dialog"
         onClick={onCancel}>
      <div className="bg-[hsl(var(--card))] rounded-xl border max-w-[720px] w-[92vw] max-h-[85vh] overflow-y-auto"
           style={{ borderColor: 'hsl(var(--border))' }}
           onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between"
             style={{ borderColor: 'hsl(var(--border))' }}>
          <div>
            <h2 className="text-[16px] font-semibold">
              Confirm and Activate — {action === 'attach' ? 'Attach to existing project' : 'Create new project'}
            </h2>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
              Nothing is written until you click <strong>Confirm and Activate</strong> below.
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {data.warnings?.length > 0 && (
            <div className="border rounded-lg p-3 bg-[hsl(48_96%_97%)]"
                 style={{ borderColor: 'hsl(38_80%_75%)' }}
                 data-testid="activate-preview-warnings">
              <div className="text-[12px] font-semibold text-[hsl(38_60%_28%)] mb-1">Please note</div>
              <ul className="text-[12px] text-[hsl(38_60%_28%)] list-disc ml-4 space-y-0.5">
                {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <section>
            <h3 className="text-[13px] font-semibold mb-2">Target project</h3>
            <div className="grid grid-cols-2 gap-2 text-[12.5px]" data-testid="activate-preview-project">
              <div><span className="text-[hsl(var(--foreground-muted))]">Name:</span> <strong>{data.project.name}</strong></div>
              <div><span className="text-[hsl(var(--foreground-muted))]">Code:</span> <strong className="font-mono">{data.project.code || '(will be auto-generated)'}</strong></div>
              <div><span className="text-[hsl(var(--foreground-muted))]">Client:</span> <strong>{data.project.client}</strong></div>
              <div><span className="text-[hsl(var(--foreground-muted))]">Stage:</span> <strong>{data.project.current_stage}</strong></div>
            </div>
          </section>

          <section>
            <h3 className="text-[13px] font-semibold mb-2">Work Order</h3>
            <div className="grid grid-cols-2 gap-2 text-[12.5px]" data-testid="activate-preview-wo">
              <div><span className="text-[hsl(var(--foreground-muted))]">WO number:</span> <strong className="font-mono">{data.wo.wo_number || '—'}</strong></div>
              <div><span className="text-[hsl(var(--foreground-muted))]">Date:</span> <strong>{data.wo.wo_date || '—'}</strong></div>
              <div className="col-span-2"><span className="text-[hsl(var(--foreground-muted))]">Fee:</span> <strong className="tabular-nums">₹ {(data.wo.total_fee_inr || 0).toLocaleString('en-IN')}</strong></div>
              {data.wo.scope_summary && (
                <div className="col-span-2 text-[11.5px] text-[hsl(var(--foreground-muted))] whitespace-pre-wrap">
                  <strong>Scope:</strong> {data.wo.scope_summary}
                </div>
              )}
            </div>
          </section>

          <section data-testid="activate-preview-plan">
            <h3 className="text-[13px] font-semibold mb-2">Records that will be created / updated</h3>
            <ul className="text-[12.5px] space-y-1">
              <li>
                <strong>{data.plan.milestones_to_seed?.length || 0}</strong> milestone(s) will be seeded
                {data.project.existing_milestones > 0 && ` (existing ${data.project.existing_milestones} preserved)`}
              </li>
              <li>
                <strong>{data.plan.qc_gates_to_seed?.length || 6}</strong> QC gate stubs will be seeded
                <span className="text-[hsl(var(--foreground-muted))]"> (advance_retainer, strategy_concept, schematic, dd, tdp, construction_admin_closure)</span>
              </li>
              <li>Work Order status will flip from <em>verified</em> → <em>activated</em>.</li>
              <li>Master Project Data will be enriched with WO fields (fill-empty-only rule).</li>
              <li>Actions, approvals, and collections views will surface this project across all dashboards.</li>
            </ul>
          </section>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2"
             style={{ borderColor: 'hsl(var(--border))' }}>
          <Button variant="ghost" onClick={onCancel} disabled={busy}
                  data-testid="activate-preview-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}
                  data-testid="activate-preview-confirm">
            <PlayCircle size={14} className="mr-1" />
            {busy ? 'Activating…' : 'Confirm and Activate'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const FieldRow = ({ label, children, score, onConfirm, testId, readOnly }) => (
  <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto_auto] gap-2 items-center" data-testid={testId}>
    <div className="text-[12px] text-[hsl(var(--foreground-muted))] font-medium">{label}</div>
    <div>{children}</div>
    <ConfidenceChip score={score} />
    {!readOnly && (score ?? 0) < 0.6 && (
      <Button size="sm" variant="outline" onClick={onConfirm} data-testid={`${testId}-confirm`}>Confirm</Button>
    )}
  </div>
);
